import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Image, 
  Video, 
  Trash2, 
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  HardDrive,
  FileImage,
  Film
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMediaPage({ admin }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, [page]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const response = await axios.get(`${API}/admin/media?page=${page}&limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setFiles(response.data.files);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.delete(`${API}/admin/media/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete file');
    } finally {
      setActionLoading(false);
    }
  };

  const isImage = (filename) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
  const isVideo = (filename) => /\.(mp4|webm|mov|avi)$/i.test(filename);

  const totalSize = files.reduce((acc, f) => acc + f.size_kb, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Media Library</h1>
          <p className="text-slate-500">{total} files uploaded</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-lg">
          <HardDrive className="w-5 h-5 text-indigo-500" />
          <span className="text-sm font-medium text-indigo-700">
            {(totalSize / 1024).toFixed(2)} MB used
          </span>
        </div>
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : files.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No media files uploaded yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {files.map((file) => (
            <div
              key={file.filename}
              className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Preview */}
              <div className="aspect-square bg-slate-100 relative">
                {isImage(file.filename) ? (
                  <img
                    src={`${BACKEND_URL}${file.url}`}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                  />
                ) : isVideo(file.filename) ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-800">
                    <Film className="w-10 h-10 text-slate-400" />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileImage className="w-10 h-10 text-slate-400" />
                  </div>
                )}

                {/* Overlay Actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a
                    href={`${BACKEND_URL}${file.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white rounded-lg hover:bg-slate-100"
                    title="Download"
                  >
                    <Download className="w-5 h-5 text-slate-700" />
                  </a>
                  <button
                    onClick={() => handleDelete(file.filename)}
                    disabled={actionLoading}
                    className="p-2 bg-white rounded-lg hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>

                {/* Type Badge */}
                <div className="absolute top-2 right-2">
                  {isVideo(file.filename) ? (
                    <Video className="w-5 h-5 text-white drop-shadow" />
                  ) : (
                    <Image className="w-5 h-5 text-white drop-shadow" />
                  )}
                </div>
              </div>

              {/* Info */}
              <div className="p-2">
                <p className="text-xs text-slate-700 truncate" title={file.filename}>
                  {file.filename}
                </p>
                <p className="text-xs text-slate-400">{file.size_kb} KB</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
